import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, ShoppingCart, ChevronLeft, ChevronRight } from 'lucide-react';
import SpicinessBadge from './SpicinessBadge';
import { formatPrice, stripHtmlToText } from '../lib/utils';
import { trackAddToCart } from '../lib/analytics';
import { useCartStore } from '../store/cartStore';
import { useToastStore } from '../store/toastStore';
import { brandLabel, isLocalOnlyCategory } from '../lib/brandMap';
import { useNaturesStore } from '../store/naturesStore';
import type { Product } from '../types';

interface ProductCardProps {
  product: Product;
  compact?: boolean;
}

export default function ProductCard({ product, compact = false }: ProductCardProps) {
  useNaturesStore((s) => s.loaded); // shipping-zones v2 定性映射載入後 🇭🇰 badge 重算
  const addItem = useCartStore((s) => s.addItem);
  const addToast = useToastStore((s) => s.addToast);
  const [currentImage, setCurrentImage] = useState(0);

  const productImages = [product.imageUrl, product.imageUrl2, product.imageUrl3].filter(Boolean) as string[];
  const hasMultipleImages = productImages.length > 1;

  const handleAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product, 1);
    trackAddToCart(product.id, 1, product.price);
    addToast(`已將「${product.name}」加入購物車`, 'success');
  };

  const goToImage = (e: React.MouseEvent, idx: number) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImage(idx);
  };

  const prevImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImage((i) => (i === 0 ? productImages.length - 1 : i - 1));
  };

  const nextImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImage((i) => (i === productImages.length - 1 ? 0 : i + 1));
  };

  if (compact) {
    return (
      <Link to={`/products/${product.slug}`} className="card group flex flex-col h-full hover:shadow-xl transition-shadow">
        <div className="relative aspect-[4/3] overflow-hidden bg-stone-100">
          {product.imageUrl ? (
            <img
              src={product.imageUrl}
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-red-50 to-red-50">
              <span className="text-4xl">🍜</span>
            </div>
          )}
          <div className="absolute top-3 left-3">
            <SpicinessBadge level={product.spicinessLevel} size="sm" />
          </div>
        </div>
        <div className="p-4 flex flex-col flex-1">
          {product.brand && (
            <span className="self-start bg-x1493-primary/10 text-x1493-primary text-[10px] font-bold px-2 py-0.5 rounded-full mb-1.5">
              {brandLabel(product.brand)}
            </span>
          )}
          {isLocalOnlyCategory(product.category) && (
            <span className="self-start bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-full mb-1.5">
              🇭🇰 只限香港
            </span>
          )}
          <h3 className="font-bold text-lg text-x1493-dark mb-1">{product.name}</h3>
          <p className="text-x1493-primary font-bold text-xl">{formatPrice(product.price)}</p>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/products/${product.slug}`} className="card group flex flex-col h-full hover:shadow-xl transition-shadow">
      <div className="relative aspect-[4/3] overflow-hidden bg-stone-100">
        {productImages.length > 0 ? (
          <img
            src={productImages[currentImage]}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-red-50 to-red-50">
            <span className="text-6xl">🍜</span>
          </div>
        )}

        <div className="absolute top-3 left-3">
          <SpicinessBadge level={product.spicinessLevel} size="sm" />
        </div>

        {product.stockQuantity <= 0 && (
          <span className="absolute top-3 right-3 bg-stone-500 text-white text-xs font-bold px-3 py-1 rounded-full">
            已售罄
          </span>
        )}

        {hasMultipleImages && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 text-stone-700 shadow opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-white"
              aria-label="上一張"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 text-stone-700 shadow opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-white"
              aria-label="下一張"
            >
              <ChevronRight size={18} />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {productImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => goToImage(e, idx)}
                  className={`w-2 h-2 rounded-full transition ${
                    idx === currentImage ? 'bg-x1493-primary' : 'bg-white/80 hover:bg-white'
                  }`}
                  aria-label={`切換到圖片 ${idx + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex flex-wrap gap-1.5 mb-2">
          {product.brand && (
            <span className="bg-x1493-primary/10 text-x1493-primary text-[11px] font-bold px-2.5 py-0.5 rounded-full">
              {brandLabel(product.brand)}
            </span>
          )}
          {isLocalOnlyCategory(product.category) && (
            <span className="bg-amber-100 text-amber-800 text-[11px] font-bold px-2.5 py-0.5 rounded-full">
              🇭🇰 只限香港
            </span>
          )}
        </div>
        <h3 className="font-bold text-xl text-x1493-dark mb-2">{product.name}</h3>
        <p className="text-sm text-stone-500 mb-3 flex-1">{(() => {
          // 描述可能係 HTML（後台可寫 <strong>/<br> 等）——摘要前先剝 tag，
          // 唔係會將原始碼同半個 tag 晒出嚟
          const text = product.description ? stripHtmlToText(product.description) : '';
          return text ? text.slice(0, 40) + (text.length > 40 ? '...' : '') : '';
        })()}</p>

        {product.suitableDishes && product.suitableDishes.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {product.suitableDishes.slice(0, 3).map((dish) => (
              <span key={dish} className="text-xs bg-stone-100 text-stone-600 px-2 py-1 rounded-lg">
                {dish}
              </span>
            ))}
            {product.suitableDishes.length > 3 && (
              <span className="text-xs text-stone-400 px-2 py-1">+{product.suitableDishes.length - 3}</span>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-stone-100">
          <span className="text-x1493-primary font-bold text-2xl">{formatPrice(product.price)}</span>
          <button
            onClick={handleAdd}
            disabled={product.stockQuantity <= 0}
            className="flex items-center gap-2 bg-x1493-primary text-white px-4 py-2 rounded-xl font-bold hover:bg-red-700 disabled:bg-stone-300 transition"
          >
            <Plus size={18} />
            加入購物車
          </button>
        </div>
      </div>
    </Link>
  );
}
